import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../utils/api";
import { FiArrowLeft, FiMail, FiPhone, FiEye, FiEyeOff } from "react-icons/fi";

const StaffForgotPassword = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState("identify"); // identify, verify, reset
  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetType, setResetType] = useState(""); // email_link or otp
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 1: IDENTIFY
  // ═══════════════════════════════════════════════════════════════════════════

  const handleInitiateForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!emailOrMobile.trim()) {
        setError("Please enter your email or mobile number");
        setLoading(false);
        return;
      }

      const response = await api.post(
        "restaurant/initiate-forgot-password",
        {
          email_or_mobile: emailOrMobile,
          user_type: "staff",
        },
        {},
        false
      );

      if (response.success || response.data) {
        setResetType(response.data?.resetType || "email_link");
        setSuccess(response.data?.message || "Reset instructions sent");
        setCurrentStep("verify");
      } else {
        setError(response.message || "Failed to initiate password reset");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2: VERIFY OTP
  // ═══════════════════════════════════════════════════════════════════════════

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!otp.trim()) {
        setError("Please enter the OTP");
        setLoading(false);
        return;
      }

      if (otp.length !== 6) {
        setError("OTP must be 6 digits");
        setLoading(false);
        return;
      }

      const response = await api.post(
        "restaurant/verify-otp",
        {
          mobile_or_email: emailOrMobile,
          otp: otp,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setResetToken(response.data?.otpId || otp);
        setSuccess("OTP verified! Now create your new password.");
        setCurrentStep("reset");
      } else {
        setError(response.message || "Invalid OTP");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 3: RESET PASSWORD
  // ═══════════════════════════════════════════════════════════════════════════

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!newPassword.trim()) {
        setError("Please enter a new password");
        setLoading(false);
        return;
      }

      if (newPassword.length < 8) {
        setError("Password must be at least 8 characters long");
        setLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      const response = await api.post(
        "restaurant/reset-password-new",
        {
          email_or_mobile: emailOrMobile,
          new_password: newPassword,
          confirm_password: confirmPassword,
          user_type: "staff",
          reset_token: resetToken,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setSuccess("Password reset successfully! Redirecting to login...");
        toast.success("Password reset successfully!");

        setTimeout(() => {
          navigate("/staff-login");
        }, 2000);
      } else {
        setError(response.message || "Failed to reset password");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: STEP 1 - IDENTIFY
  // ═══════════════════════════════════════════════════════════════════════════

  if (currentStep === "identify") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <FiArrowLeft size={20} />
            Back
          </button>

          <h1 className="text-2xl font-bold text-gray-800 mb-2">Forgot Password?</h1>
          <p className="text-gray-600 text-sm mb-6">
            Enter your email or mobile number to reset your password
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleInitiateForgotPassword} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email or Mobile Number
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400">
                  {emailOrMobile.includes("@") ? (
                    <FiMail size={20} />
                  ) : (
                    <FiPhone size={20} />
                  )}
                </span>
                <input
                  type="text"
                  value={emailOrMobile}
                  onChange={(e) => setEmailOrMobile(e.target.value)}
                  placeholder="example@email.com or 9876543210"
                  autoComplete="off"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? "Sending..." : "Send Reset Instructions"}
            </button>
          </form>

          <p className="text-center text-gray-600 text-sm mt-6">
            Remember your password?{" "}
            <button
              onClick={() => navigate("/staff-login")}
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              Login
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: STEP 2 - VERIFY OTP
  // ═══════════════════════════════════════════════════════════════════════════

  if (currentStep === "verify") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <button
            onClick={() => setCurrentStep("identify")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <FiArrowLeft size={20} />
            Back
          </button>

          <h1 className="text-2xl font-bold text-gray-800 mb-2">Enter OTP</h1>
          <p className="text-gray-600 text-sm mb-6">
            Enter the 6-digit OTP sent to {emailOrMobile}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                OTP Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                autoComplete="off"
                className="w-full px-4 py-2 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-gray-500 text-xs mt-2">OTP expires in 10 minutes</p>
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </form>

          <p className="text-center text-gray-600 text-sm mt-6">
            Didn't receive OTP?{" "}
            <button
              onClick={handleInitiateForgotPassword}
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              Resend
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: STEP 3 - RESET PASSWORD
  // ═══════════════════════════════════════════════════════════════════════════

  if (currentStep === "reset") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <button
            onClick={() => setCurrentStep("verify")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <FiArrowLeft size={20} />
            Back
          </button>

          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Create New Password
          </h1>
          <p className="text-gray-600 text-sm mb-6">
            Enter a strong password to secure your account
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    autoComplete="new-password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  autoComplete="new-password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-2">Password Requirements:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li className={newPassword.length >= 8 ? "text-green-600" : "text-gray-600"}>
                  ✓ At least 8 characters
                </li>
                <li
                  className={
                    newPassword !== confirmPassword || !confirmPassword
                      ? "text-gray-600"
                      : "text-green-600"
                  }
                >
                  ✓ Passwords match
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    );
  }
};

export default StaffForgotPassword;
